import useFilterService from 'api/services/filterService'
import usePropertyService from 'api/services/propertyService'
import { buildPropertyFilterParams } from 'api/utils/buildPropertyFilterParams'
import FilterBar from 'components/filterBar/FilterBar'
import { isPriceRangeValid } from 'components/filterBar/FilterPriceRange'
import { RESIDENTIAL_UNIT_TYPE_OPTIONS } from 'components/filterBar/residentialUnitTypes'
import PropertyList from 'components/listings/Listings'
import {
  buildActiveFilterSummary,
  bumpPriceMax,
  consumeRestoredFilters,
  filtersWithBumpedPriceMax,
  filtersWithoutNeighborhoods,
} from 'components/listings/listingsEmptyHelpers'
import ListingsEmptyState, {
  ListingsEmptySuggestion,
} from 'components/listings/ListingsEmptyState'
import ListingsSortSelect, {
  DEFAULT_LISTING_SORT,
} from 'components/listings/ListingsSortSelect'
import { ListingSortOption, sortListings } from 'components/listings/sortListings'
import useListingFavorite from 'components/propertyCard/useListingFavorite'
import ViewToggle from 'components/ViewToggle'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { Filters } from 'types/general'
import { ListedProperty } from 'types/property'

const defaultFilters: Filters = {
  bedrooms: [],
  bathrooms: [],
  unitType: [],
  price: [],
  priceMin: undefined,
  priceMax: undefined,
  location: {
    zip_code: '',
    full_address: '',
    range: 10,
    city: '',
    neighborhood: '',
    neighborhoods: [],
  },
  tags: [],
  booleans: {
    verified: false,
    heat: false,
    parking: false,
    petFriendly: false,
    electricity: false,
    internet: false,
    snowRemoval: false,
    furnished: false,
    semiFurnished: false,
  },
  contractEnd: { startDate: '', endDate: '' },
  availability: { startDate: '', endDate: '' },
}

const ListingsPage = () => {
  const { loadListedProperties } = usePropertyService()
  const { loadFilterOptions } = useFilterService()
  const { t } = useTranslation()
  const [draftFilters, setDraftFilters] = useState(defaultFilters)
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters)
  const [sortBy, setSortBy] = useState<ListingSortOption>(DEFAULT_LISTING_SORT)

  useEffect(() => {
    const restored = consumeRestoredFilters()
    if (!restored) return
    setDraftFilters({ ...defaultFilters, ...restored, location: { ...defaultFilters.location, ...restored.location }, booleans: { ...defaultFilters.booleans, ...restored.booleans } })
    setAppliedFilters({ ...defaultFilters, ...restored, location: { ...defaultFilters.location, ...restored.location }, booleans: { ...defaultFilters.booleans, ...restored.booleans } })
  }, [])

  const filterParams = useMemo(
    () => buildPropertyFilterParams(appliedFilters),
    [appliedFilters]
  )
  const filterQueryKey =
    filterParams === null ? 'all' : JSON.stringify(filterParams)

  const { data: listedProperties, isLoading, isFetching, isPreviousData } = useQuery(
    ['properties', filterQueryKey],
    () => loadListedProperties(filterParams) || [],
    {
      keepPreviousData: true,
      staleTime: 5 * 60_000,
    }
  )

  // Show skeletons when filters change (previous results kept until new data arrives).
  const isResultsLoading = isLoading || (isFetching && Boolean(isPreviousData))

  const { data: filterOptions, isLoading: isLoadingFilterOptions } = useQuery(
    'filterOptions',
    loadFilterOptions
  )

  const sortedProperties = useMemo(
    () => sortListings((listedProperties as ListedProperty[] | undefined) ?? [], sortBy),
    [listedProperties, sortBy]
  )

  const { handleFavorite, loadingFavoriteId, favoriteEpoch } = useListingFavorite({
    properties: sortedProperties,
  })

  const displayProperties = useMemo(
    () => sortedProperties.map(property => ({ ...property })),
    [sortedProperties, favoriteEpoch]
  )

  const updateFilters = (next: Filters) => {
    setDraftFilters(next)
    // Apply immediately; skip invalid price ranges so we don't fire bad queries.
    if (isPriceRangeValid(next.priceMin, next.priceMax)) {
      setAppliedFilters(next)
    }
  }

  const onResetFilters = () => {
    setDraftFilters(defaultFilters)
    setAppliedFilters(defaultFilters)
  }

  const applyFiltersNow = (next: Filters) => {
    setDraftFilters(next)
    setAppliedFilters(next)
  }

  const hasAppliedFilters = filterParams !== null
  const resultsCount = sortedProperties.length
  const showEmptyState = !isResultsLoading && resultsCount === 0
  const emptyMode = hasAppliedFilters ? 'filtered' : 'catalog'

  const resultsCountKey =
    resultsCount === 1
      ? hasAppliedFilters
        ? 'listing.filteredCount_one'
        : 'listing.availableCount_one'
      : hasAppliedFilters
        ? 'listing.filteredCount_other'
        : 'listing.availableCount_other'
  const resultsCountLabel = isResultsLoading
    ? t('listing.loadingResults')
    : t(resultsCountKey, { count: resultsCount })

  const activeUnitTypes = appliedFilters.unitType ?? []
  const removeUnitType = (value: string) => {
    const nextUnitTypes = activeUnitTypes.filter(item => item !== value)
    const nextApplied = { ...appliedFilters, unitType: nextUnitTypes }
    const nextDraft = { ...draftFilters, unitType: nextUnitTypes }
    setAppliedFilters(nextApplied)
    setDraftFilters(nextDraft)
  }

  const filterSummary = useMemo(
    () => buildActiveFilterSummary(appliedFilters, t),
    [appliedFilters, t]
  )

  const hasNeighborhoodFilter =
    (appliedFilters.location?.neighborhoods?.length ?? 0) > 0 ||
    Boolean(appliedFilters.location?.neighborhood?.trim())
  const hasCity = Boolean(appliedFilters.location?.city?.trim())
  const cityOnlyFilters = useMemo(
    () => (hasNeighborhoodFilter && hasCity ? filtersWithoutNeighborhoods(appliedFilters) : null),
    [appliedFilters, hasNeighborhoodFilter, hasCity]
  )
  const cityOnlyParams = useMemo(
    () => (cityOnlyFilters ? buildPropertyFilterParams(cityOnlyFilters) : null),
    [cityOnlyFilters]
  )

  const { data: cityOnlyListings } = useQuery(
    ['properties', 'empty-suggest-city', JSON.stringify(cityOnlyParams)],
    () => loadListedProperties(cityOnlyParams) || [],
    {
      enabled: showEmptyState && cityOnlyParams !== null,
      staleTime: 5 * 60_000,
    }
  )

  const bumpedMax =
    appliedFilters.priceMax !== undefined && Number.isFinite(appliedFilters.priceMax)
      ? bumpPriceMax(appliedFilters.priceMax)
      : null
  const priceRelaxedFilters = useMemo(
    () =>
      bumpedMax !== null && bumpedMax > (appliedFilters.priceMax ?? 0)
        ? filtersWithBumpedPriceMax(appliedFilters, bumpedMax)
        : null,
    [appliedFilters, bumpedMax]
  )
  const priceRelaxedParams = useMemo(
    () => (priceRelaxedFilters ? buildPropertyFilterParams(priceRelaxedFilters) : null),
    [priceRelaxedFilters]
  )

  const { data: priceRelaxedListings } = useQuery(
    ['properties', 'empty-suggest-price', JSON.stringify(priceRelaxedParams)],
    () => loadListedProperties(priceRelaxedParams) || [],
    {
      enabled: showEmptyState && priceRelaxedParams !== null,
      staleTime: 5 * 60_000,
    }
  )

  const suggestions = useMemo(() => {
    if (!showEmptyState) return [] as ListingsEmptySuggestion[]
    const items: ListingsEmptySuggestion[] = []

    const cityCount = Array.isArray(cityOnlyListings) ? cityOnlyListings.length : 0
    if (cityOnlyFilters && cityCount > 0) {
      const cityName = appliedFilters.location?.city?.trim() || ''
      const neighborhoodLabel =
        (appliedFilters.location?.neighborhoods ?? []).join(', ') ||
        appliedFilters.location?.neighborhood ||
        ''
      items.push({
        id: 'nearby-city',
        title: t('listing.empty.suggestNeighborhoodTitle', { neighborhood: neighborhoodLabel }),
        description: t(
          cityCount === 1
            ? 'listing.empty.suggestNeighborhoodBody_one'
            : 'listing.empty.suggestNeighborhoodBody_other',
          { count: cityCount, city: cityName }
        ),
        ctaLabel: t('listing.empty.suggestNeighborhoodCta', { city: cityName }),
        onApply: () => applyFiltersNow(cityOnlyFilters),
      })
    }

    const priceCount = Array.isArray(priceRelaxedListings) ? priceRelaxedListings.length : 0
    if (priceRelaxedFilters && bumpedMax !== null && priceCount > 0) {
      const currentMax = appliedFilters.priceMax!
      items.push({
        id: 'price-bump',
        title: t('listing.empty.suggestPriceTitle', {
          min: appliedFilters.priceMin ?? 0,
          max: currentMax,
        }),
        description: t(
          priceCount === 1
            ? 'listing.empty.suggestPriceBody_one'
            : 'listing.empty.suggestPriceBody_other',
          { count: priceCount, max: bumpedMax }
        ),
        ctaLabel: t('listing.empty.suggestPriceCta', { max: bumpedMax }),
        onApply: () => applyFiltersNow(priceRelaxedFilters),
      })
    }

    return items
  }, [
    showEmptyState,
    cityOnlyListings,
    cityOnlyFilters,
    priceRelaxedListings,
    priceRelaxedFilters,
    bumpedMax,
    appliedFilters,
    t,
  ])

  const approximateProperties = useMemo(() => {
    if (!showEmptyState) return [] as ListedProperty[]
    const cityList = Array.isArray(cityOnlyListings)
      ? (cityOnlyListings as ListedProperty[])
      : []
    if (cityList.length > 0) return cityList.slice(0, 6)
    const priceList = Array.isArray(priceRelaxedListings)
      ? (priceRelaxedListings as ListedProperty[])
      : []
    return priceList.slice(0, 6)
  }, [showEmptyState, cityOnlyListings, priceRelaxedListings])

  const onEditFilters = () => {
    const el = document.getElementById('listings-filters')
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    window.dispatchEvent(new Event('openMobileFilters'))
  }

  if (isLoading && listedProperties == null) {
    return (
      <div className="p-8 max-w-[1600px] mx-auto">
        <div className="mb-4 h-8 w-64 animate-pulse rounded bg-gray-200" />
        <PropertyList properties={[]} isLoading />
      </div>
    )
  }

  return (
    <div className="p-8 max-w-[1600px] mx-auto">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-imovPrimary">
            {t('common.leaseTransfers')}
          </h1>
          <p className="mt-1 text-sm text-imovSecondary/70" aria-live="polite">
            {resultsCountLabel}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <ListingsSortSelect value={sortBy} onChange={setSortBy} />
          <ViewToggle />
        </div>
      </div>

      {activeUnitTypes.length > 0 || hasAppliedFilters ? (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {activeUnitTypes.map(value => {
            const option = RESIDENTIAL_UNIT_TYPE_OPTIONS.find(item => item.value === value)
            const label = option ? t(option.label) : value
            return (
              <button
                key={value}
                type="button"
                onClick={() => removeUnitType(value)}
                className="inline-flex items-center gap-1.5 rounded-full border border-imovPrimary/30 bg-imovPrimary/10 px-3 py-1.5 text-xs font-medium text-imovPrimary"
              >
                <span>{label}</span>
                <span aria-hidden className="text-sm leading-none">
                  ×
                </span>
                <span className="sr-only">{t('filters.removeActiveFilter', { label })}</span>
              </button>
            )
          })}
          {hasAppliedFilters ? (
            <button
              type="button"
              onClick={onResetFilters}
              className="text-xs font-semibold text-imovPrimary hover:underline"
            >
              {t('filters.clearAll')}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="flex items-start gap-8">
        {!isLoadingFilterOptions && filterOptions && (
          <>
            {/* Desktop filters expand to full content height; page scroll shows listings */}
            <div
              id="listings-filters"
              className="hidden w-full max-w-xs flex-shrink-0 scroll-mt-6 self-start lg:block"
            >
              <FilterBar
                variant="desktop"
                filters={draftFilters}
                setFilters={updateFilters}
                filterOptions={filterOptions}
                onReset={onResetFilters}
                hasAppliedFilters={hasAppliedFilters}
              />
            </div>
            {/* Mobile: floating button + sheet (no fixed sidebar) */}
            <div className="lg:hidden">
              <FilterBar
                variant="mobile"
                filters={draftFilters}
                setFilters={updateFilters}
                filterOptions={filterOptions}
                onReset={onResetFilters}
                hasAppliedFilters={hasAppliedFilters}
              />
            </div>
          </>
        )}

        <div className="min-w-0 flex-1">
          {showEmptyState ? (
            <ListingsEmptyState
              mode={emptyMode}
              filterSummary={filterSummary}
              criteria={appliedFilters}
              suggestions={suggestions}
              approximateProperties={approximateProperties}
              onEditFilters={onEditFilters}
              onClearFilters={onResetFilters}
              onFavorite={handleFavorite}
              loadingFavoriteId={loadingFavoriteId}
            />
          ) : (
            <PropertyList
              properties={displayProperties}
              handleFavorite={handleFavorite}
              loadingFavoriteId={loadingFavoriteId}
              returnTo="/listings"
              isLoading={isResultsLoading}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ListingsPage
